import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'tertiary' | 'destructive';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  loading?: boolean;
  fullWidth?: boolean;
};

const base =
  'inline-flex min-h-touch items-center justify-center gap-2 rounded-pill px-6 text-lg font-semibold transition-[transform,background-color,opacity] duration-150 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100';

const variants: Record<Variant, string> = {
  primary: 'bg-brand-teal text-ink-on-teal active:bg-brand-teal-deep',
  secondary: 'border-2 border-brand-teal bg-surface text-brand-teal active:bg-brand-teal-soft',
  tertiary: 'bg-transparent text-brand-teal underline-offset-4 active:underline',
  destructive: 'bg-state-danger text-ink-on-teal',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', loading = false, fullWidth = false, disabled, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(base, variants[variant], fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
});

function Spinner() {
  return (
    <span
      aria-hidden
      className="size-5 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}
